import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { readString } from 'react-papaparse';
import styled from 'styled-components';
import Button from '../../atoms/Buttons';
import FA from '../../atoms/FontAwesome';
import Grid from '../../atoms/Grid';
import Label from '../../atoms/Label';
import TextBox from '../../atoms/TextBox';

type Props = {
  miniumNumberForCombinationPurchase: number;
  productBasedPointRules: ProductBasedPointRule[];
  onMiniumNumberForCombinationPurchaseChange?: (value: number) => void;
  onProductBasedPointRulesChange?: (rules: ProductBasedPointRule[]) => void;
  disabled?: boolean;
};

export type ProductBasedPointRule = {
  product_code: string;
  point_amount: number;
  point_amount_unit: 'percent' | 'absolute';
  starts_at?: string;
  ends_at?: string;
  required_count?: number;
  is_multiply_by_count?: boolean;
};

const fileupStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: 400,
  padding: 16,
  margin: '12px 0',
  backgroundColor: '#F8F7F6',
} as const;

const DragAndDropInstruction = styled.p`
  font-size: 18px;
  color: #706d65;
  text-align: center;
  line-height: 1.5;
`;

const FileSelectButton = styled(Button)`
  color: black;
  font-size: 18px;
  padding: 8px 16px;
  border-radius: 8px;
  margin: 0 auto;
`;

const borderNormalStyle = {
  border: '1px dashed #D6D3D0',
};
const borderDragStyle = {
  border: '1px solid #D6D3D0',
  transition: 'border .5s ease-in-out',
};

const ProductRuleWrapper = styled(Grid.Item)`
  border: solid 1px #ececec;
  padding: 8px;
`;

const ProductBasedPointRules = ({
  miniumNumberForCombinationPurchase,
  productBasedPointRules,
  onMiniumNumberForCombinationPurchaseChange,
  onProductBasedPointRulesChange,
  disabled = false,
}: Props) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      const { type } = file;
      if (type !== 'text/csv') {
        return;
      }
      const reader = new FileReader();
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          return;
        }
        const binaryStr = reader.result;
        readString<string[]>(binaryStr, {
          worker: true,
          skipEmptyLines: true,
          complete: (results) => {
            const [_header, ...data] = results.data;

            const products = data.map((row) => ({
              product_code: row[0],
              point_amount: Number(row[1]),
              point_amount_unit: row[2] === '1' ? ('percent' as const) : ('absolute' as const),
              ...(row[3] ? { starts_at: row[3] } : {}),
              ...(row[4] ? { ends_at: row[4] } : {}),
              ...(row[5] ? { required_count: Number(row[5]) } : {}),
              ...(row[6] ? { is_multiply_by_count: row[6] === '1' } : {}),
            }));

            onProductBasedPointRulesChange?.(products);
          },
        });
      };
      reader.readAsText(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
  });

  const uploadstyle = useMemo(
    () => ({
      ...fileupStyle,
      ...(isDragActive ? borderDragStyle : borderNormalStyle),
    }),
    [isDragActive]
  );

  return (
    <>
      {!disabled && (
        <div {...getRootProps({ style: uploadstyle })}>
          <input {...getInputProps()} />
          <DragAndDropInstruction>
            ここにドラッグ&ドロップ <br /> または
          </DragAndDropInstruction>
          <FileSelectButton type="button" onClick={open} variant="muted-outline">
            <FA icon="plus-circle" /> ファイルを選択
          </FileSelectButton>
        </div>
      )}
      <Grid.Container rowGap={8}>
        {productBasedPointRules.length > 1 && (
          <Grid.Item
            size={12}
            flexDirection="row"
            alignItems="center"
            gap="8px"
            style={{ marginTop: '8px' }}
          >
            <Label>複数種類の商品を同時購入するときの商品種別数の下限</Label>
            <TextBox
              type="number"
              pattern="\d+(?:\.\d+)?"
              value={miniumNumberForCombinationPurchase}
              onChange={(e) => onMiniumNumberForCombinationPurchaseChange?.(Number(e.target.value))}
              disabled={disabled}
            />
          </Grid.Item>
        )}
        {productBasedPointRules.map((rule, index) => (
          <ProductRuleWrapper nextRow size={12} container columnGap={16}>
            <Grid.Item size={4} alignCenter>
              対象商品コード
            </Grid.Item>
            <Grid.Item size={8} alignCenter>
              {rule.product_code}
            </Grid.Item>

            <Grid.Item size={4} alignCenter>
              ポイント額
            </Grid.Item>
            <Grid.Item size={8} alignCenter>
              {rule.point_amount}
            </Grid.Item>

            <Grid.Item size={4} alignCenter>
              単位
            </Grid.Item>
            <Grid.Item size={8} alignCenter>
              {rule.point_amount_unit === 'percent' ? 'パーセント' : '絶対'}
            </Grid.Item>

            {!!rule.starts_at && (
              <>
                <Grid.Item size={4} alignCenter>
                  開始時間
                </Grid.Item>
                <Grid.Item size={8} alignCenter>
                  {moment(rule.starts_at).format('YYYY/MM/DD HH:mm')}
                </Grid.Item>
              </>
            )}

            {!!rule.ends_at && (
              <>
                <Grid.Item size={4} alignCenter>
                  終了時間
                </Grid.Item>
                <Grid.Item size={8} alignCenter>
                  {moment(rule.ends_at).format('YYYY/MM/DD HH:mm')}
                </Grid.Item>
              </>
            )}

            {!!rule.required_count && (
              <>
                <Grid.Item size={4} alignCenter>
                  適用対象購入個数
                </Grid.Item>
                <Grid.Item size={8} alignCenter>
                  {rule.required_count}
                </Grid.Item>
              </>
            )}

            <Grid.Item size={4} alignCenter>
              複数個購入したときにポイント付与額に個数を掛ける
            </Grid.Item>
            <Grid.Item size={8} alignCenter>
              {!!rule.is_multiply_by_count ? 'はい' : 'いいえ'}
            </Grid.Item>

            <Grid.Item size={12}>
              <Button
                onClick={() =>
                  onProductBasedPointRulesChange?.(
                    productBasedPointRules.filter((_, i) => i !== index)
                  )
                }
                disabled={disabled}
              >
                削除
              </Button>
            </Grid.Item>
          </ProductRuleWrapper>
        ))}
      </Grid.Container>
    </>
  );
};

export default ProductBasedPointRules;
